/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useState } from 'react';
import { jsx, css } from '@emotion/react';
import useAxios from 'axios-hooks';
import { Spinner, Pivot, PivotItem } from '@fluentui/react';
import { Formik } from 'formik';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import WizardModeIndicator from '../../../Util/WizardModeIndicator';
import { 
    validationSchema, mapInitialValues, route, basePath, 
    successMsg, setErrorMsg, rootCss, unsavedMsg, setStatusHelpMsg
 } from '../Helpers/CopyAndFunctions';
import useDataState from '../../../Util/useDataState';
import useHandleSave from '../../../Util/useHandleSave';
import FormStepWrapper from '../../../Forms/FormStepWrapper';
import BasicInfoFields from '../Components/BasicInfoFields';
import PresenterDetailFields from '../Components/PresenterDetailFields';
import AudienceFields from '../Components/AudienceFields';
import PublishStatusFields from '../Components/PublishStatusFields';
import MessageBarComponent from '../../../Util/MessageBarComponent';
import FeatureSummary from '../../../Util/FeatureSummary';

const webinarsSummary = [
    {
        selected: "0",
        label: "Basic Info",
        summary: "Set the webinar title, description, date/time, registration URL, and platform settings. This is the core information displayed on the webinar card.",
    },
    {
        selected: "1",
        label: "Presenter Details",
        summary: "Add presenter information including name, title, bio, and photo. This information appears on the webinar detail page.",
    },
    {
        selected: "2",
        label: "Audience",
        summary: "Configure which cobrands and countries can access this webinar. Target specific user segments for your webinar content.",
    },
    {
        selected: "3",
        label: "Publish Status",
        summary: "Control the webinar's visibility by setting its publish status. Draft webinars are not visible to end users.",
    },
];

const WizardPage = () => {
    const { webinarId: idFromParams } = useParams();
    const {
        data: webinarData,
        isReady,
        submitForm,
        showSuccess,
        showError,
        setShowError,
        setShowSuccess,
    } = useDataState(idFromParams, route, basePath);

    const [{ data: qnsData, qnsDataLoading }] = useAxios('webinars/webinar-qns');
    const [{ data: openEndedQnsData, loading: loadingOpenEndedQns }] = useAxios('webinars/webinar-open-ended-qns');
    const [{ loading: loadingCountries, data: countries }] = useAxios('courses/countries');

    const [selectedKey, setSelectedKey] = useState('0');

    if (!isReady || qnsDataLoading || loadingOpenEndedQns || loadingCountries) {
        return <Spinner className='absolute-center' text='Loading Webinar Settings...' />;
    }

    return (
        <>
            <WizardModeIndicator paramId={idFromParams} title="Webinar" />
            <Formik
            validateOnMount={true}
            initialValues={mapInitialValues(webinarData)}
            onSubmit={async (values, { resetForm }) => {
                if (!values.webinarId) values.webinarId = 0;
                if (values.cardBannerUrl) {
                    values.cardBannerUrl = values.cardBannerUrl.split('/').pop();
                }
                if (!values.platformId) values.platformId = 1;
                if (!values.publishStatusId) values.publishStatusId = 1;
                values.crsPublishStatusID = values.publishStatusId;

                const webinarDateTime = format(new Date(values.webinarDateTime), "yyyy-MM-dd'T'HH:mm:ss");
                await submitForm({ data: { ...values, webinarDateTime } }, resetForm, 'webinarId');
            }}
            isInitialValid={false}
            validationSchema={validationSchema}
        >
            {({ errors, values, isValid, handleSubmit }) => {
                const handleSave = useHandleSave(setShowError);
                const summaryObj = webinarsSummary.find(s => String(s.selected) === String(selectedKey));
                const handleLinkClick = (item) => {
                    if (item?.props?.itemKey) {
                        setSelectedKey(item.props.itemKey);
                    }
                }

                return values ? (
                    <div css={css`max-width: 800px;`}>
                        <MessageBarComponent 
                            message={setStatusHelpMsg(values.publishStatus)} 
                            showMessage={showError} 
                            setShowMessage={setShowError} 
                            isMultiline
                        />
                        <FormStepWrapper
                            handleSave={() => handleSave(isValid, handleSubmit)}
                            showSuccess={showSuccess}
                            showError={showError}
                            successMessage={successMsg}
                            errorMessage={setErrorMsg(showError, errors)}
                            unsavedMsg={unsavedMsg}
                            rootCss={rootCss}
                            showNavigation={true}
                            firstStep={0}
                            lastStep={3}
                            setShowSuccess={setShowSuccess}
                            setShowError={setShowError}
                            selectedKey={selectedKey}
                            setSelectedKey={setSelectedKey}
                        >
                            <Pivot selectedKey={selectedKey} onLinkClick={handleLinkClick}>
                                <PivotItem headerText='Basic Info' itemKey='0'>
                                    <BasicInfoFields qnsData={qnsData} openEndedQnsData={openEndedQnsData} />
                                </PivotItem>
                                <PivotItem headerText='Presenter Details' itemKey='1'>
                                    <PresenterDetailFields />
                                </PivotItem>
                                <PivotItem headerText='Audience' itemKey='2'>
                                    <AudienceFields countries={countries} />
                                </PivotItem>
                                <PivotItem headerText='Publish Status' itemKey='3'>
                                    <PublishStatusFields />
                                </PivotItem>
                            </Pivot>
                        </FormStepWrapper>
                        {summaryObj && (
                            <FeatureSummary
                                label={summaryObj.label}
                                summary={summaryObj.summary}
                                imageUrlPath={summaryObj.imageUrlPath}
                            />
                        )}
                    </div>
                        
                    ) : null;
            }}
        </Formik>
        </>
    );
};

export default WizardPage;
